/**
 * useDeadlineImpression
 *
 * Fires an anonymous impression to /api/deadlines/{id}/impression the
 * first time the target element enters the viewport. Deduped per
 * (session_key, deadline_id, day) on the backend, so this only
 * needs a stable session_key across the browser tab's lifetime.
 *
 * Usage:
 *   const ref = useDeadlineImpression(deadline.id, { source: "uni_teaser" });
 *   return <div ref={ref}>…</div>;
 */
import { useEffect, useRef } from "react";
import { api } from "@/lib/api";


function getOrMintSessionKey() {
  const K = "wua.session.impr";
  try {
    let v = sessionStorage.getItem(K);
    if (!v) {
      v = "s-" + Math.random().toString(36).slice(2) + Date.now().toString(36);
      sessionStorage.setItem(K, v);
    }
    return v;
  } catch {
    // Private-mode / disabled storage — fall back to an in-memory key.
    if (!window.__wua_impr_key) {
      window.__wua_impr_key = "s-mem-" + Math.random().toString(36).slice(2);
    }
    return window.__wua_impr_key;
  }
}


export function useDeadlineImpression(deadlineId, { source } = {}) {
  const ref = useRef(null);
  const firedRef = useRef(false);

  useEffect(() => {
    if (!deadlineId || firedRef.current || !ref.current) return;
    if (typeof IntersectionObserver === "undefined") return;
    const el = ref.current;
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting && !firedRef.current) {
            firedRef.current = true;
            io.disconnect();
            api.post(`/deadlines/${deadlineId}/impression`, {
              session_key: getOrMintSessionKey(),
              source: source || null,
            }).catch(() => { /* silent */ });
            break;
          }
        }
      },
      { threshold: 0.5 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [deadlineId, source]);

  return ref;
}


export function trackDeadlineClick(deadlineId, source) {
  if (!deadlineId) return;
  api.post(`/deadlines/${deadlineId}/click`, {
    session_key: getOrMintSessionKey(),
    source: source || null,
  }).catch(() => { /* silent */ });
}
